import React, { FC, useCallback, useEffect } from "react";
import StyledDrawer from "@components/StyledDrawer";
import { Reminder } from "@models/reminder";
import { ActivityType } from "@models/activity-type";
import TextIconButton from "@components/TextIconButton";
import { useTranslation } from "react-i18next";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import ReminderForm from "@components/ReminderForm";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import HomeFormButtons from "@components/Home/HomeFormButtons/HomeFormButtons";
import { useForm } from "react-hook-form";
import { ReminderFormType } from "@components/ReminderForm/ReminderForm";
import { CronUtil } from "@utils/cron.util";
import { DayOfWeek } from "@models/day-of-week";
import { ReminderFrequency } from "@models/reminder-frequency";

const DayOfWeekNumber: Record<DayOfWeek, number> = {
  [DayOfWeek.Sunday]: 0,
  [DayOfWeek.Monday]: 1,
  [DayOfWeek.Tuesday]: 2,
  [DayOfWeek.Wednesday]: 3,
  [DayOfWeek.Thursday]: 4,
  [DayOfWeek.Friday]: 5,
  [DayOfWeek.Saturday]: 6,
};
const DaysOfWeek: DayOfWeek[] = [
  DayOfWeek.Sunday,
  DayOfWeek.Monday,
  DayOfWeek.Tuesday,
  DayOfWeek.Wednesday,
  DayOfWeek.Thursday,
  DayOfWeek.Friday,
  DayOfWeek.Saturday,
];

const ReminderDrawer: FC<{
  open: boolean;
  onClose: () => void;
  reminder?: Reminder;
  onDelete: () => void;
  onSave: (values: Omit<Reminder, "id" | "patientId">) => Promise<void>;
}> = ({ open, onClose, reminder, onSave, onDelete }) => {
  const { t } = useTranslation();

  const form = useForm<ReminderFormType>();
  const {
    clearErrors,
    reset,
    handleSubmit,
    formState: { isSubmitting, isValid },
  } = form;

  const resetForm = useCallback(
    (reminder?: Reminder) => {
      const { time, frequency, dayOfWeek } = (
        reminder ? CronUtil.parseCron(reminder.schedule) : {}
      ) as {
        dayOfWeek: number[];
        frequency: ReminderFrequency;
        time: Date;
      };

      reset({
        title: reminder ? reminder.title : "",
        description: reminder ? reminder.description : "",
        startDate: reminder?.startDate ? new Date(reminder.startDate) : null,
        activityType: reminder
          ? reminder.activityType
          : ActivityType.Conversation,
        isActive: reminder ? reminder.isActive : false,
        time: time || null,
        frequency: frequency || ReminderFrequency.OneTime,
        dayOfWeek: (dayOfWeek
          ? Object.values(DayOfWeek).reduce(
              (res, curr: DayOfWeek) => {
                const index = DaysOfWeek.indexOf(curr);
                res[curr] = dayOfWeek.includes(index);
                return res;
              },
              {} as Record<DayOfWeek, boolean>,
            )
          : {}) as Record<DayOfWeek, boolean>,
      });
    },
    [reset],
  );

  useEffect(() => {
    resetForm(reminder);
  }, [resetForm, reminder]);

  const onSubmit = async (formValue: ReminderFormType) => {
    if (!isValid) {
      return;
    }
    let dayOfWeek = formValue.dayOfWeek
      ? Object.entries(formValue.dayOfWeek)
          .filter(([_, selected]) => selected)
          .map(([day]) => DayOfWeekNumber[day as DayOfWeek])
      : Object.values(DayOfWeekNumber);
    const schedule = CronUtil.generateCron({
      dayOfWeek,
      frequency: formValue.frequency!,
      time: formValue.time!,
      date: formValue.startDate!,
    });

    const reminder = {
      title: formValue.title!,
      description: formValue.description!,
      activityType: formValue.activityType!,
      startDate: formValue.startDate!,
      isActive: formValue.isActive,
      schedule,
    };
    await onSave(reminder);

    resetForm();
  };

  return (
    <StyledDrawer
      onClose={onClose}
      open={open}
      width="600px"
      slots={{
        topRight: reminder && (
          <TextIconButton
            variant="outlined"
            color="error"
            text={t("schedule.drawer.deleteButton")}
            icon={DeleteOutlineOutlinedIcon}
            onClick={onDelete}
          />
        ),
      }}
    >
      <form
        onSubmit={(e) => {
          clearErrors();
          return handleSubmit(onSubmit)(e);
        }}
      >
        <ReminderForm form={form} />

        <HomeFormButtons>
          <TextIconButton
            type="submit"
            disabled={isSubmitting}
            variant="contained"
            size="large"
            text={t(`schedule.drawer.saveButton`)}
            icon={SaveOutlinedIcon}
          />
        </HomeFormButtons>
      </form>
    </StyledDrawer>
  );
};

export default ReminderDrawer;
